/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash/fp/compose';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { jsx } from '@emotion/core';
import { Icon } from '../../ui/Icon';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import { withNamespaces } from 'react-i18next';
import { hideElementCss } from '../../../utils/responsive';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import { MediaQueries } from '../../../styles/Grid';
import Picture from '../../ui/Picture';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  LOGO_WIDTH,
  LOGO_HEIGHT,
  IMAGE_WIDTH,
  IMAGE_HEIGHT,
  IMAGE_MARGIN,
} from './constants';
import {
  arrowBtnCss,
  buttonsContainerCss,
  captionTitleCss,
  captionTitleContainerCss,
  captionWrapperCss,
  companyLogoCss,
  imageCss,
  itemCaptionCss,
  itemContainerCss,
  itemsWrapperCss,
  leftArrowBtnCss,
  linkCss,
  rightArrowBtnCss,
  titleCss,
  titleContainerCss,
} from './index.styles';

const PREV = 'prev';
const NEXT = 'next';
const MIN_IMAGES_TO_SHOW = 2;
const CAROUSEL_CHANGE_STEP = 1;

const itemImageSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    width: IMAGE_WIDTH,
    height: IMAGE_HEIGHT,
    aspectRatio: '4:3',
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    width: IMAGE_WIDTH,
    height: IMAGE_HEIGHT,
    aspectRatio: '4:3',
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    width: IMAGE_WIDTH,
    height: IMAGE_HEIGHT,
    aspectRatio: '4:3',
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    width: IMAGE_WIDTH,
    height: IMAGE_HEIGHT,
    aspectRatio: '4:3',
  },
};

const companyLogoSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    width: LOGO_WIDTH,
    height: LOGO_HEIGHT,
    crop: 'pad',
    gravity: 'center',
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    width: LOGO_WIDTH,
    height: LOGO_HEIGHT,
    crop: 'pad',
    gravity: 'center',
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    width: LOGO_WIDTH,
    height: LOGO_HEIGHT,
    crop: 'pad',
    gravity: 'center',
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    width: LOGO_WIDTH,
    height: LOGO_HEIGHT,
    crop: 'pad',
    gravity: 'center',
  },
};

class Carousel extends SafePureComponent {
  static propTypes = {
    items: PropTypes.shape({
      value: PropTypes.arrayOf(
        PropTypes.shape({
          CarouselImage: PropTypes.shape({
            value: PropTypes.shape({
              src: PropTypes.string,
              alt: PropTypes.string,
              caption: PropTypes.string,
            }),
          }),
          CarouselCompanyName: PropTypes.shape({
            value: PropTypes.string,
          }),
          CarouselCompanyLogo: PropTypes.shape({
            value: PropTypes.shape({
              src: PropTypes.string,
              alt: PropTypes.string,
            }),
          }),
          CarouselImageCaption: PropTypes.shape({
            value: PropTypes.string,
          }),
          CarouselLink: PropTypes.shape({
            value: PropTypes.shape({
              href: PropTypes.string,
              text: PropTypes.string,
            }),
          }),
        }),
      ),
    }),
    customCss: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.itemsLength = get(props.items, 'value.length', 0);

    this.state = {
      activeIndex: 0,
      renderControls: this.itemsLength > 1,
    };
  }

  getItemsWrapperWidth = () => ({
    width: this.itemsLength * (IMAGE_WIDTH + IMAGE_MARGIN),
  });

  getLeftPosition = () => ({
    left: -(this.state.activeIndex * (IMAGE_WIDTH + IMAGE_MARGIN)),
  });

  changeImage = action => {
    let currentActiveIndex = this.state.activeIndex;
    let canChangeImage = true;

    if (action === PREV) {
      currentActiveIndex -= CAROUSEL_CHANGE_STEP;
    } else {
      currentActiveIndex += CAROUSEL_CHANGE_STEP;
    }

    if (
      window.innerWidth >= Breakpoints.md &&
      this.itemsLength - currentActiveIndex < MIN_IMAGES_TO_SHOW
    ) {
      canChangeImage = false;
    }

    if (
      currentActiveIndex >= 0 &&
      currentActiveIndex < this.itemsLength &&
      canChangeImage
    ) {
      this.setState({
        activeIndex: currentActiveIndex,
      });
    }
  };

  renderButtons = () => {
    let buttonsContainerCustomCss = {};

    if (this.itemsLength === MIN_IMAGES_TO_SHOW) {
      buttonsContainerCustomCss = hideElementCss([MediaQueries.mdAndUp]);
    }

    return (
      <div css={[buttonsContainerCss, buttonsContainerCustomCss]}>
        <button
          css={[arrowBtnCss, leftArrowBtnCss]}
          name="Previous image"
          aria-label="Previous image"
          onClick={() => this.changeImage(PREV)}
        >
          <Icon name="arrow-left" />
        </button>
        <button
          css={[arrowBtnCss, rightArrowBtnCss]}
          name="Next image"
          aria-label="Next image"
          onClick={() => this.changeImage(NEXT)}
        >
          <Icon name="arrow-right" />
        </button>
      </div>
    );
  };

  renderItems = (item, index) => {
    let WrapperComponent = React.Fragment;
    let wrapperComponentProps;
    const carouselLink = get(item, 'CarouselLink.value.href');
    const carouselTarget = !isEmpty(get(item, 'CarouselLink.value.target'))
      ? item.CarouselLink.value.target
      : '_blank';

    if (Boolean(carouselLink)) {
      WrapperComponent = 'a';
      wrapperComponentProps = {
        href: carouselLink,
        css: linkCss,
        target: carouselTarget,
      };
    }

    return (
      <li css={[itemContainerCss, this.getLeftPosition()]} key={index}>
        <WrapperComponent {...wrapperComponentProps}>
          <div>
            <Picture
              image={item.CarouselImage}
              imageCss={imageCss}
              imageSizes={itemImageSizes}
              instantLoad
            />
          </div>
          {this.renderCaption(item)}
        </WrapperComponent>
      </li>
    );
  };

  renderCaption = item => {
    const companyLogoSrc = get(item, 'CarouselCompanyLogo.value.src');

    return (
      <div css={captionWrapperCss}>
        <div css={captionTitleContainerCss}>
          {Boolean(companyLogoSrc) && (
            <Picture
              image={item.CarouselCompanyLogo}
              imageCss={companyLogoCss}
              imageSizes={companyLogoSizes}
              instantLoad
            />
          )}
          {!Boolean(companyLogoSrc) && (
            <h3 css={captionTitleCss}>
              {get(item, 'CarouselCompanyName.value')}
            </h3>
          )}
        </div>
        <div>
          <p css={itemCaptionCss}>{get(item, 'CarouselImageCaption.value')}</p>
        </div>
      </div>
    );
  };

  safeRender() {
    const { t, items, customCss } = this.props;
    const { renderControls } = this.state;

    return (
      <div css={customCss}>
        <div css={titleContainerCss}>
          <h2 css={titleCss}>{t('MoreStoriesTitle')}</h2>
          {renderControls && this.renderButtons()}
        </div>
        <ul css={[itemsWrapperCss, this.getItemsWrapperWidth()]}>
          {get(items, 'value', []).map(this.renderItems)}
        </ul>
      </div>
    );
  }
}

Carousel.displayName = 'Carousel';

export default compose(withNamespaces())(Carousel);
